import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { motion } from "framer-motion";
import LanguageToggle from "@/components/LanguageToggle";
import { Search, CheckCircle2, Stethoscope, User, Loader2, RefreshCw, SlidersHorizontal, Mic, ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";

type PractitionerType = "dietitian" | "doctor";
const doctorSpecialties = [
  "all",
  "general medicine",
  "cardiology",
  "gynecology",
  "dermatology",
  "pediatrics",
  "neurology",
  "orthopedics",
  "psychiatry",
  "gastroenterology",
  "ent",
] as const;

interface Practitioner {
  id: string;
  user_id: string;
  full_name: string;
  role: PractitionerType;
  specialization: string | null;
  rating: number | null;
  reviews_count: number | null;
  experience: number | null;
  fee: number | null;
  available: boolean | null;
}

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.1 } } };
const fadeUp = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

const FindDietitian = () => {
  const { language } = useLanguage();
  const { user } = useAuth();
  const location = useLocation();
  const bn = language === "bn";
  const [searchQuery, setSearchQuery] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState<(typeof doctorSpecialties)[number]>("all");
  const [requestedIds, setRequestedIds] = useState<string[]>([]);
  const [filterType, setFilterType] = useState<PractitionerType>(
    location.pathname === "/find-doctor" ? "doctor" : "dietitian",
  );
  const [practitioners, setPractitioners] = useState<Practitioner[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [requestingId, setRequestingId] = useState<string | null>(null);
  const { toast } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setLoadError(null);
      const [practitionerResponse, requestResponse] = await Promise.all([
        supabase.from("profiles").select("*").in("role", ["dietitian", "doctor"]),
        user
          ? supabase.from("consultation_requests").select("practitioner_id").eq("patient_id", user.id).eq("status", "pending")
          : Promise.resolve({ data: [], error: null }),
      ]);

      if (practitionerResponse.error) {
        setLoadError(practitionerResponse.error.message);
        setPractitioners([]);
      } else {
        setPractitioners((practitionerResponse.data ?? []) as Practitioner[]);
      }
      if (requestResponse.error) {
        setLoadError(requestResponse.error.message);
      } else {
        setRequestedIds((requestResponse.data ?? []).map((request) => request.practitioner_id));
      }
      setLoading(false);
    };
    load();
  }, [user]);

  useEffect(() => {
    setFilterType(location.pathname === "/find-doctor" ? "doctor" : "dietitian");
  }, [location.pathname]);

  const filtered = practitioners.filter((d) => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = d.full_name.toLowerCase().includes(q) || (d.specialization ?? "").toLowerCase().includes(q);
    if (!matchesSearch || d.role !== filterType) return false;
    const specialization = (d.specialization ?? "").toLowerCase();
    if (filterType === "doctor" && specialtyFilter !== "all" && !specialization.includes(specialtyFilter)) return false;

    return true;
  });

  const handleRequest = async (d: Practitioner) => {
    if (!user || requestingId) return;
    setRequestingId(d.user_id);
    setRequestedIds((prev) => [...prev, d.user_id]);

    const { error } = await supabase.rpc("create_initial_consultation_request", {
      _practitioner_id: d.user_id,
      _practitioner_type: d.role,
      _reason: null,
    });

    if (error) {
      setRequestedIds((prev) => prev.filter((id) => id !== d.user_id));
      toast({
        title: bn ? "অনুরোধ পাঠানো যায়নি" : "Request could not be sent",
        description: error.message,
        variant: "destructive",
      });
      setRequestingId(null);
      return;
    }

    toast({
      title: bn ? "অনুরোধ পাঠানো হয়েছে ✓" : "Request Sent ✓",
      description: bn ? `${d.full_name}-কে পরামর্শের অনুরোধ পাঠানো হয়েছে` : `Consultation request sent to ${d.full_name}`,
    });
    setRequestingId(null);
  };

  return (
    <div className="min-h-screen bg-[#fbfcf8] pb-28">
      <section className="relative overflow-hidden border-b border-[#e4eee7] bg-[#f1f8f3] px-5 pb-4 pt-5">
        <div className="relative z-20 flex justify-end">
          <LanguageToggle />
        </div>
        <div className="relative mt-5 min-h-[128px]">
          <div className="relative z-10 max-w-[53%]">
            <h1 className="font-serif text-[30px] font-extrabold leading-tight tracking-tight text-[#174e49]">
              {bn ? "যত্ন খুঁজুন" : "Find care"}
            </h1>
            <p className="mt-2 text-sm leading-5 text-[#5d6f6b]">
              {bn ? "সঠিক স্বাস্থ্যসেবা পেশাদারের সাথে যোগাযোগ করুন" : "Connect with the right healthcare professional"}
            </p>
          </div>
          <img
            src="/care-consultation.png"
            alt=""
            className="pointer-events-none absolute -bottom-8 -right-8 z-0 h-[176px] w-[285px] object-contain object-right-bottom"
          />
        </div>
        <div className="relative z-10 flex gap-1 rounded-full border border-[#dcebe2] bg-white p-0.5">
          {(["dietitian", "doctor"] as const).map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilterType(f)}
              className={`flex-1 rounded-full py-2 text-xs font-bold transition-all ${
                filterType === f ? "bg-[#138b72] text-white" : "text-[#5d6f6b]"
              }`}
            >
              {f === "dietitian" ? (bn ? "ডায়েটিশিয়ান" : "Dietitian") : (bn ? "ডাক্তার" : "Doctor")}
            </button>
          ))}
        </div>
      </section>

      <div className="space-y-5 px-5 pt-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#62736f]" />
          <input
            type="text"
            placeholder={bn ? "নাম বা বিশেষত্ব দিয়ে খুঁজুন..." : "Search by name or specialty..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full rounded-full border border-[#e1e9e4] bg-white py-3.5 pl-11 pr-12 text-sm font-medium text-foreground outline-none placeholder:text-[#8b9894] focus:border-[#2c8979]"
          />
          <Mic className="absolute right-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#168c74]" />
        </div>

        <div className="flex items-center gap-2">
          <button type="button" className="flex items-center gap-1.5 rounded-full bg-[#e1f1eb] px-3 py-2 text-xs font-bold text-[#226f63]">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {bn ? "সব" : "All"}
          </button>
          <button type="button" className="flex items-center gap-1.5 rounded-full border border-[#e2e9e5] bg-white px-3 py-2 text-xs font-bold text-[#3e4f4b]">
            <span className="h-1.5 w-1.5 rounded-full bg-[#55b58b]" />
            {bn ? "উপলব্ধ" : "Available"}
          </button>
          {filterType === "doctor" && (
            <label className="flex items-center gap-1 rounded-full border border-[#e2e9e5] bg-white px-2.5 py-1.5 text-xs font-bold text-[#3e4f4b]">
              <span className="sr-only">{bn ? "বিশেষত্ব বাছাই করুন" : "Choose specialty"}</span>
              <select
                value={specialtyFilter}
                onChange={(event) => setSpecialtyFilter(event.target.value as (typeof doctorSpecialties)[number])}
                className="max-w-[130px] bg-transparent outline-none"
              >
                {doctorSpecialties.map((specialty) => (
                  <option key={specialty} value={specialty}>
                    {specialty === "all"
                      ? (bn ? "সব বিশেষত্ব" : "All specialties")
                      : specialty.replace(/\b\w/g, (letter) => letter.toUpperCase())}
                  </option>
                ))}
              </select>
              <ChevronDown className="h-3.5 w-3.5" />
            </label>
          )}
          <button type="button" className="ml-auto flex items-center gap-1 rounded-full border border-[#e2e9e5] bg-white px-3 py-2 text-xs font-bold text-[#3e4f4b]">
            {bn ? "সাজান" : "Sort"} <ChevronDown className="h-3.5 w-3.5" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : loadError ? (
          <div className="rounded-2xl border border-[#e2ebe4] bg-white p-6 text-center">
            <p className="text-sm font-bold text-destructive">{bn ? "ডেটা লোড করা যায়নি" : "Could not load practitioners"}</p>
            <p className="mt-2 text-xs text-muted-foreground">{loadError}</p>
            <button onClick={() => window.location.reload()} className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-4 py-2 text-xs font-bold text-primary-foreground">
              <RefreshCw className="h-3.5 w-3.5" />{bn ? "আবার চেষ্টা করুন" : "Try again"}
            </button>
          </div>
        ) : (
          <motion.div className="space-y-3" variants={stagger} initial="hidden" animate="show">
            <div>
              <h2 className="font-serif text-base font-extrabold text-[#243b37]">
                {filterType === "doctor" ? (bn ? "উপলব্ধ ডাক্তার" : "Available doctors") : (bn ? "উপলব্ধ ডায়েটিশিয়ান" : "Available dietitians")}
              </h2>
              <p className="mt-1 text-xs text-[#788783]">
                {bn ? "একজন পেশাদার বেছে নিয়ে পরামর্শের অনুরোধ করুন" : "Choose a professional and request a consultation"}
              </p>
            </div>
            {filtered.length === 0 && (
              <div className="py-16 text-center text-sm text-muted-foreground">{bn ? "কোনো ফলাফল নেই" : "No results found"}</div>
            )}
            {filtered.map((d) => {
              const requested = requestedIds.includes(d.user_id);
              return (
                <motion.div key={d.id} variants={fadeUp} className="rounded-[18px] border border-[#e2ebe4] bg-white p-4">
                  <div className="flex gap-3">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#e1f1eb] text-[#166f62]">
                      {d.role === "doctor" ? <Stethoscope className="h-6 w-6" /> : <User className="h-6 w-6" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-extrabold text-[#233a36]">{d.full_name}</h3>
                            <span className="rounded-full bg-[#e1f1eb] px-1.5 py-0.5 text-[8px] font-bold text-[#2e796a]">
                              {d.role === "doctor" ? (bn ? "ডাক্তার" : "Doctor") : (bn ? "ডায়েটিশিয়ান" : "Dietitian")}
                            </span>
                          </div>
                          <p className="mt-0.5 text-xs text-[#788783]">{d.specialization ?? ""}</p>
                        </div>
                      </div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <span className="rounded-full bg-[#e9f4ef] px-2 py-1 text-[10px] font-bold text-[#39796b]">
                          {d.experience ?? 0} {bn ? "বছর অভিজ্ঞতা" : "yrs experience"}
                        </span>
                        <span className={`rounded-full px-2 py-1 text-[10px] font-bold ${d.available ? "bg-[#e1f1eb] text-[#2e806d]" : "bg-[#fbe9e8] text-destructive"}`}>
                          {d.available ? (bn ? "উপলব্ধ" : "Available") : (bn ? "ব্যস্ত" : "Busy")}
                        </span>
                      </div>
                      <div className="mt-3 flex items-center justify-between gap-2">
                        <div>
                          <span className="text-lg font-extrabold text-[#233a36]">৳{d.fee ?? 0}</span>
                          <span className="ml-1 text-[10px] text-[#788783]">/{bn ? "সেশন" : "session"}</span>
                        </div>
                        <motion.button
                          onClick={() => requested ? navigate("/patient-inbox") : handleRequest(d)}
                          className={`rounded-full px-4 py-2.5 text-xs font-bold ${
                            requested ? "bg-[#e1f1eb] text-[#2e806d]" : "bg-[#138b72] text-white"
                          }`}
                          whileTap={{ scale: 0.95 }}
                          disabled={!d.available || requestingId === d.user_id}
                        >
                          {requested ? (
                            <span className="flex items-center gap-1"><CheckCircle2 className="h-3 w-3" /> {bn ? "অনুরোধ পাঠানো" : "Requested"}</span>
                          ) : requestingId === d.user_id
                            ? (bn ? "পাঠানো হচ্ছে..." : "Sending...")
                            : (bn ? "অনুরোধ পাঠান" : "Request Consult")}
                        </motion.button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default FindDietitian;
